import lodashGet from 'lodash.get';

import { Column } from '@tanstack/react-table';
import { CSSProperties } from 'react';
import { TableTrophy } from './table-creator';

/**
 * @description
 * Classe responsável por centralizar as regras de estilo e layout das colunas da tabela.
 * Inclui suporte para colunas fixadas (pinning), cores indicativas e lógica de exibição de troféus.
 */
export class TableLayoutHelper {
  static shouldShowTrophy(
    columnDef: any,
    row: any,
    bestValues: Record<string, any>
  ): boolean {
    const trophy = columnDef?.meta?.trophy;
    if (!trophy) return false;

    const columnKey = columnDef.accessorKey;
    const cellValue = lodashGet(row.original, columnKey);

    return (
      (trophy === TableTrophy.HIGHEST && cellValue === bestValues[columnKey]) ||
      (trophy === TableTrophy.LOWEST && cellValue === bestValues[columnKey])
    );
  }

  static getHeaderStyles({
    header,
    backgroundColor,
    textColor,
    columnSizingInfo,
  }: {
    header: any;
    backgroundColor?: string;
    textColor?: string;
    columnSizingInfo: any;
  }) {
    return {
      backgroundColor,
      color: textColor,
      position: 'relative',
      width: header.getSize(),
      maxWidth: header.getSize(),
      textAlign: (header.column.columnDef?.meta as any)?.align,
      pointerEvents: columnSizingInfo.isResizingColumn ? 'none' : 'auto',
      ...TableLayoutHelper.getCommonPinningStyles(header.column),
    } as CSSProperties;
  }

  static getHeaderContentStyles({ header }: { header: any }) {
    const convertTextAlignIntoJustify = {
      default: 'flex-start',
      left: 'flex-start',
      right: 'flex-end',
      center: 'center',
    } as const;

    const align = ((header.column.columnDef?.meta as any)?.align ||
      'default') as keyof typeof convertTextAlignIntoJustify;

    return {
      display: 'flex',
      gap: '4px',
      alignItems: 'center',
      justifyContent: convertTextAlignIntoJustify[align],
    } as CSSProperties;
  }

  static getCellStyles({ cell }: { cell: any }) {
    const shouldHighlight = cell.getContext()?.row?.original?.contrastRow;

    return {
      fontFamily: 'var(--monospace)',
      textAlign: (cell.column.columnDef?.meta as any)?.align,
      width: cell.column.getSize(),
      maxWidth: cell.column.getSize(),
      ...(shouldHighlight && { fontWeight: 600, '& > p': { fontWeight: 600 } }),
      ...TableLayoutHelper.getCommonPinningStyles(cell.column),
    };
  }

  //These are the important styles to make sticky column pinning work!
  //Apply styles like this using your CSS strategy of choice with this kind of logic to head cells, data cells, footer cells, etc.
  //View the index.css file for more needed styles such as border-collapse: separate
  private static getCommonPinningStyles(column: Column<any>): CSSProperties {
    const isPinned = column.getIsPinned();
    const isLastLeftPinnedColumn =
      isPinned === 'left' && column.getIsLastColumn('left');
    const isFirstRightPinnedColumn =
      isPinned === 'right' && column.getIsFirstColumn('right');

    return {
      boxShadow: isLastLeftPinnedColumn
        ? '-3px 0 4px -2px #00000033 inset'
        : isFirstRightPinnedColumn
        ? '3px 0 4px -2px #00000033 inset'
        : undefined,
      left: isPinned === 'left' ? `${column.getStart('left')}px` : undefined,
      right: isPinned === 'right' ? `${column.getAfter('right')}px` : undefined,
      position: isPinned ? 'sticky' : 'relative',
      width: column.getSize(),
      maxWidth: column.columnDef.maxSize,
      zIndex: isPinned ? 1 : 0,
    };
  }
}
